import moment from 'moment';
import { useSelector } from 'react-redux';
import { UserProfession } from '~/enums/UserProfession';
import { isPartialShift } from '../../../commonComponents/CommonFunctions';
import { Colors } from '../../../themes/colors';
import { EducationUtils } from '../../../utils/EducationUtils';
import { PartialShift } from '../../CandidateList/components/PartialShift';
import { renderOfferExpirationDateTime } from '../../counterJobs/RenderFunc';
import Tooltip from '../../Tooltip';
import { isEmployeeOptionShift } from '../../WorkerClassification/utils';
import { HighlightedLines } from './HighlightedLines';

const convenienceFeeTooltip =
  'GoTu charges a 3.5% convenience fee on Credit Card transactions. If you do not wish to be charged the convenience fee, please update your billing to E-check/ACH.';

const overtimeTooltip =
  'Overtime pay (1.5x rate) is required for hours that exceed the state’s daily and/or weekly regular working hour limits.';

export const ShiftDetails = ({ currentCounter }) => {
  const user = useSelector((state) => state.user.selectedChildOffice ?? state.user.user);
  const job = useSelector((state) => state.job.job);
  const selectedOffice = useSelector((store) => store.user.selectedOffice);
  const {
    profession,
    job_datetime: jobDateTime,
    tm_fee_percent: tmFeePercent,
    specialty,
    local_date: localDate,
    local_start_time: localStartTime,
    local_end_time: localEndTime,
    rate,
    employeeOptionFee,
    offer_owner: offerOwner,
  } = job || {};

  const { payment } = currentCounter || {};

  const isCounterHours = currentCounter ? isPartialShift(currentCounter) : null;
  const isCounterRate = currentCounter?.candidateRate !== rate;
  const chargeDate = moment(jobDateTime).subtract(28, 'days').format('MM/DD/YYYY');
  const startDiff = moment(jobDateTime).diff(moment(), 'days');

  const totalAmount = payment?.totalAmount;
  const tempmeeFee = payment?.tempmeeFee;
  const tempmeeFeePercent = payment?.tempmeeFeeRatio
    ? (payment?.tempmeeFeeRatio * 100).toFixed(1)
    : 0;
  const convenienceFee = currentCounter?.payment?.convenienceFee ?? 0;
  const convenienceFeePercent = currentCounter?.payment?.convenienceFeeRatio
    ? (currentCounter?.payment?.convenienceFeeRatio * 100).toFixed(1)
    : 0;

  const professionName = (() => {
    if (job.subProfession === 'EFDA') {
      return 'Expanded Functions DA';
    }

    return job.professionName;
  })();

  return (
    <div
      className="global_font"
      style={{
        display: 'flex',
        columnGap: 20,
        justifyContent: 'center',
        width: '100%',
        marginBottom: 0,
      }}
    >
      <div
        style={{
          flex: 1,
          maxWidth: 600,
          display: 'flex',
          flexDirection: 'column',
          padding: '24px 24px 40px',
          rowGap: 12,
          backgroundColor: Colors.neutral_50,
        }}
      >
        {isCounterHours && (
          <div
            data-cy="counter-hours-indicator"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: -8,
              marginTop: 8,
            }}
          >
            <PartialShift />
          </div>
        )}

        {renderOfferExpirationDateTime(currentCounter?.expiration, user?.time_zone)}

        <h1
          style={{
            fontSize: 20,
            margin: '0 0 16px',
            color: Colors.primary_500,
            textAlign: 'center',
          }}
        >
          <b>Shift Details</b>
        </h1>

        <div
          style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
        >
          <b>Office:</b>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 200,
            }}
          >
            {offerOwner?.office_name || selectedOffice?.office_name}
          </span>
        </div>

        <div
          style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
        >
          <b>Date:</b>
          <span>{localDate}</span>
        </div>

        <div
          style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
        >
          <b>Profession:</b>
          <span>{professionName}</span>
        </div>

        <div
          style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
        >
          <b>{profession === UserProfession.DN ? 'Shift Specialty:' : 'Preferred Specialty:'}</b>
          <span
            style={{
              textTransform: 'capitalize',
              textAlign: 'right',
            }}
          >
            {EducationUtils.removeSpecialtyPrefix(specialty) || 'General'}
          </span>
        </div>

        <HighlightedLines highlight={isCounterHours}>
          <div
            data-cy="detail-row-original-hours"
            style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <b>Hours:</b>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: Colors.neutral_300,
                  height: 'min-content',
                  padding: '0 5px',
                  marginLeft: 8,
                  borderRadius: 50,
                }}
              >
                <b
                  style={{
                    color: Colors.white,
                    fontSize: 12,
                  }}
                >
                  EST
                </b>
              </div>
            </div>

            <span
              data-cy="original-hours-value"
              style={{
                textDecoration: isCounterHours ? 'line-through' : 'none',
              }}
            >
              {`${localStartTime} - ${localEndTime}`}
            </span>
          </div>

          {isCounterHours && (
            <div
              data-cy="detail-row-hours"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: Colors.neutral_600,
              }}
            >
              <b>*Counter Offer (hours):</b>
              <b data-cy="hours-value" style={{ textTransform: 'lowercase' }}>
                {`${currentCounter?.local_begin} - ${currentCounter?.local_end}`}
              </b>
            </div>
          )}
        </HighlightedLines>

        {isCounterHours && <div style={{ marginTop: -20 }} />}

        <HighlightedLines highlight={isCounterRate}>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
          >
            <b>Hourly Rate:</b>
            <span
              data-cy="original-rate"
              style={{
                textDecoration:
                  job?.rate !== currentCounter?.candidateRate ? 'line-through' : 'none',
              }}
            >
              ${rate.toFixed(2)} /hr
            </span>
          </div>

          {isCounterRate ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: Colors.neutral_600,
              }}
            >
              <b>*Counter Offer (rate):</b>
              <b data-cy="counter-rate">${currentCounter?.candidateRate?.toFixed(2)} /hr</b>
            </div>
          ) : (
            <></>
          )}
        </HighlightedLines>

        <div
          style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
        >
          <b>Meal Break:</b>
          <span>
            {job.lunch_final_break_time} min {job.is_lunch_break_paid ? ' (Paid)' : ' (Unpaid)'}
          </span>
        </div>

        <HighlightedLines highlight={job?.regularAndOvertimeHours?.overtime > 0}>
          {job?.regularAndOvertimeHours?.regular > 0 && job?.totalRegular > 0 && (
            <div
              data-cy="detail-row-regular-pay"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: Colors.neutral_600,
              }}
            >
              <b>Regular Rate Pay (<span data-cy="working-hours-value">{job?.regularAndOvertimeHours?.regular?.toFixed(2)}</span> hrs):</b>

              <span data-cy="regular-pay-value">{`$${job?.totalRegular?.toFixed(2)}`}</span>
            </div>
          )}
          {job?.regularAndOvertimeHours?.overtime > 0 && job?.totalOvertime > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: Colors.neutral_600,
              }}
            >
              <b style={{ display: 'flex' }}>
                *Overtime Pay ({job?.regularAndOvertimeHours?.overtime?.toFixed(2)} hrs):
                <span
                  style={{
                    marginLeft: '5px',
                    position: 'relative',
                    bottom: '2.5px',
                    marginBottom: -10,
                  }}
                >
                  <Tooltip content={overtimeTooltip} />
                </span>
              </b>

              <b>{`$${job?.totalOvertime?.toFixed(2)}`}</b>
            </div>
          )}
        </HighlightedLines>

        <div
          data-cy="detail-row-service-fee"
          style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
        >
          {tempmeeFeePercent !== 0 ? (
            <b>
              Service Fee ({tempmeeFeePercent}
              %):
            </b>
          ) : (
            <b>Service Fee:</b>
          )}

          <span data-cy="service-fee">{`$${tempmeeFee?.toFixed(2)}`}</span>
        </div>

        {payment?.peoFee !== 0 && isEmployeeOptionShift(job) && (
          <div
            style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
          >
            <b>Employee Fee:</b>
            <span className="global_font">${payment?.peoFee?.toFixed(2)}</span>
          </div>
        )}

        {convenienceFeePercent !== 0 && convenienceFee ? (
          <div
            data-cy="detail-row-convenience-fee"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: -8,
              color: Colors.neutral_600,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <b>Convenience Fee {`(${convenienceFeePercent || '0'}%)`}:</b>
              <span
                style={{
                  marginLeft: '5px',
                  position: 'relative',
                  bottom: '2.5px',
                }}
              >
                <Tooltip content={convenienceFeeTooltip} />
              </span>
            </div>
            <span data-cy="convenience-fee">{`$${convenienceFee?.toFixed(2) || '0'}`}</span>
          </div>
        ) : null}

        {job?.multi_day_booking && startDiff > 28 ? (
          <div
            style={{ display: 'flex', justifyContent: 'space-between', color: Colors.neutral_600 }}
          >
            <b>Professional Pay Charge Date:</b>
            <span>{chargeDate}</span>
          </div>
        ) : null}

        {job?.counterCover ? (
          <div
            data-cy="detail-row-counter-cover"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              color: Colors.neutral_600,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <b data-cy="counter-cover-label">Gotu Counter Cover:</b>
            </div>
            <span data-cy="counter-cover-value">-$ {job.counterCover.amount.toFixed(2)}</span>
          </div>
        ) : null}

        <div
          data-cy="detail-row-total-pay"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: Colors.neutral_600,
          }}
        >
          <b
            style={{
              color: Colors.success_500,
              display: 'flex',
              alignItems: 'center',
              columnGap: 5,
              fontSize: 16,
            }}
          >
            Total Pay:
          </b>

          <b data-cy="total-pay" style={{ color: Colors.neutral_600 }}>${totalAmount?.toFixed(2)}</b>
        </div>
      </div>
    </div>
  );
};
